import type { Action, Workflow } from '@/types/shared'
import { actionEdgeRefs, actionInputBinIds, resolveActionOutputBinIds } from '@/types/shared'

/**
 * A workflow as the canvas sees it: the Actions that belong together, the
 * bins they are wired to, and the notes, variables, and assets their briefs
 * tag directly. Backed by a `workflows` record when the group has been named
 * or created on purpose; otherwise derived from the wiring (Actions that
 * share a bin are one workflow) so agent-built chains that predate the
 * record still get a frame.
 */
export interface WorkflowGroup {
  /** Stable id for the frame node: the record id, or the seed action id before naming. */
  id: string
  /** The `workflows` record id, or null for a wiring-derived group. */
  workflowId: string | null
  name: string
  sceneId: string
  /** Member action ids in run order. */
  actionIds: string[]
  /** Bins any member reads or writes, deduped, in member order. */
  binIds: string[]
  /**
   * Engine node ids of the notes, variables, and assets any member's brief
   * tags directly (`annotation-…`, `variable-…`, `asset-…`), deduped, in
   * member order. Bins are in `binIds`; models connect nothing.
   */
  inputNodeIds: string[]
}

export const DEFAULT_WORKFLOW_NAME = 'Workflow'

/** The engine node id of a group's frame. */
export function workflowFrameNodeId(groupId: string): string {
  return `workflow-frame-${groupId}`
}

function byOrder(a: Action, b: Action): number {
  return a.order - b.order || a.id.localeCompare(b.id)
}

function wiredBins(actions: readonly Action[]): string[] {
  const seen = new Set<string>()
  for (const action of actions) {
    for (const binId of actionInputBinIds(action)) seen.add(binId)
    for (const binId of resolveActionOutputBinIds(action)) seen.add(binId)
  }
  return [...seen]
}

/** The engine node a non-bin input ref renders as (same prefixes the edge sync uses). */
export function inputRefNodeId(ref: { type: string; id: string }): string | null {
  switch (ref.type) {
    case 'asset':
      return `asset-${ref.id}`
    case 'variable':
      return `variable-${ref.id}`
    case 'note':
      return `annotation-${ref.id}`
    default:
      return null
  }
}

function taggedInputNodeIds(actions: readonly Action[]): string[] {
  const seen = new Set<string>()
  for (const action of actions) {
    for (const ref of actionEdgeRefs(action)) {
      const nodeId = inputRefNodeId(ref)
      if (nodeId) seen.add(nodeId)
    }
  }
  return [...seen]
}

/**
 * Group a scene's Actions into workflows. Explicit `workflowId`s win; the
 * rest are joined by shared bins (union-find over wiring). Output is sorted
 * so the frame list is stable across syncs.
 */
export function deriveWorkflowGroups(
  actions: Iterable<Action>,
  workflows: ReadonlyMap<string, Workflow>,
  sceneId: string,
): WorkflowGroup[] {
  const inScene = [...actions].filter(action => action.sceneId === sceneId).sort(byOrder)
  const groups: WorkflowGroup[] = []

  const explicit = new Map<string, Action[]>()
  const loose: Action[] = []
  for (const action of inScene) {
    if (action.workflowId) {
      const members = explicit.get(action.workflowId) ?? []
      members.push(action)
      explicit.set(action.workflowId, members)
    } else {
      loose.push(action)
    }
  }
  for (const [workflowId, members] of explicit) {
    groups.push({
      id: workflowId,
      workflowId,
      name: workflows.get(workflowId)?.name?.trim() || DEFAULT_WORKFLOW_NAME,
      sceneId,
      actionIds: members.map(action => action.id),
      binIds: wiredBins(members),
      inputNodeIds: taggedInputNodeIds(members),
    })
  }

  // Wiring-derived groups: union actions that touch the same bin.
  const parent = new Map<string, string>()
  const find = (id: string): string => {
    let root = id
    while (parent.get(root) !== root) root = parent.get(root) ?? root
    let cursor = id
    while (cursor !== root) {
      const next = parent.get(cursor) ?? root
      parent.set(cursor, root)
      cursor = next
    }
    return root
  }
  const union = (a: string, b: string): void => {
    const ra = find(a)
    const rb = find(b)
    if (ra !== rb) parent.set(ra < rb ? rb : ra, ra < rb ? ra : rb)
  }
  const binOwner = new Map<string, string>()
  for (const action of loose) {
    parent.set(action.id, action.id)
    for (const binId of wiredBins([action])) {
      const owner = binOwner.get(binId)
      if (owner) union(owner, action.id)
      else binOwner.set(binId, action.id)
    }
  }
  const components = new Map<string, Action[]>()
  for (const action of loose) {
    const root = find(action.id)
    const members = components.get(root) ?? []
    members.push(action)
    components.set(root, members)
  }
  for (const members of components.values()) {
    members.sort(byOrder)
    const seed = [...members.map(action => action.id)].sort()[0]
    groups.push({
      id: seed,
      workflowId: null,
      name: DEFAULT_WORKFLOW_NAME,
      sceneId,
      actionIds: members.map(action => action.id),
      binIds: wiredBins(members),
      inputNodeIds: taggedInputNodeIds(members),
    })
  }

  return groups.sort((a, b) => a.id.localeCompare(b.id))
}

/** The group a set of selected actions belongs to, when they all share one. */
export function findGroupForActions(
  groups: readonly WorkflowGroup[],
  actionIds: readonly string[],
): WorkflowGroup | null {
  let group: WorkflowGroup | null = null
  for (const id of actionIds) {
    const candidate = groups.find(candidate => candidate.actionIds.includes(id))
    // A remote delete can leave a stale action id in the local selection.
    if (!candidate) continue
    if (group && group.id !== candidate.id) return null
    group = candidate
  }
  return group
}

/**
 * Each Action's step number inside its own workflow (1-based, run order).
 * `Action.order` is unique per canvas — a second placed workflow continues
 * the count — so what people see is the rank within the group, never the
 * stored order.
 */
export function workflowStepNumbers(
  actions: Iterable<Action>,
  workflows: ReadonlyMap<string, Workflow>,
): Map<string, number> {
  const list = [...actions]
  const numbers = new Map<string, number>()
  for (const sceneId of new Set(list.map(action => action.sceneId)))
    for (const group of deriveWorkflowGroups(list, workflows, sceneId))
      group.actionIds.forEach((id, index) => numbers.set(id, index + 1))
  return numbers
}

/**
 * The step number for an Action that is on the canvas. Every canvas Action
 * belongs to exactly one group, so a miss is a broken projection, not a
 * state to paper over — it throws rather than quietly renumbering.
 */
export function stepNumberFor(numbers: ReadonlyMap<string, number>, actionId: string): number {
  const step = numbers.get(actionId)
  if (step === undefined) throw new Error(`No workflow step number for action ${actionId}`)
  return step
}
